// Helper functions
const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error'
};

// Urgency countdown timer (global, storage-backed)
const URGENCY_TIMER_STORAGE_KEY = 'tec:urgencyTimer:endAt';
const URGENCY_TIMER_DURATION_KEY = 'tec:urgencyTimer:durationMs';
const URGENCY_TIMER_MEMORY_END_KEY = '__tecUrgencyEndAt';
const URGENCY_TIMER_MEMORY_DURATION_KEY = '__tecUrgencyDurationMs';

function safeStorageGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeStorageSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {}
}

function nowMs() {
  return Date.now ? Date.now() : new Date().getTime();
}

function getOrStartUrgencyEndAt(durationMs) {
  const parsedDuration = Number(durationMs) > 0 ? Number(durationMs) : 180000; // default 3m
  const storedEnd = Number(safeStorageGet(URGENCY_TIMER_STORAGE_KEY));
  const memEnd = Number(window[URGENCY_TIMER_MEMORY_END_KEY]);
  const now = nowMs();
  const candidate = (!storedEnd || Number.isNaN(storedEnd)) ? memEnd : storedEnd;
  if (!candidate || Number.isNaN(candidate) || candidate <= now) {
    const newEnd = now + parsedDuration;
    safeStorageSet(URGENCY_TIMER_STORAGE_KEY, String(newEnd));
    safeStorageSet(URGENCY_TIMER_DURATION_KEY, String(parsedDuration));
    window[URGENCY_TIMER_MEMORY_END_KEY] = newEnd;
    window[URGENCY_TIMER_MEMORY_DURATION_KEY] = parsedDuration;
    return newEnd;
  }
  // persist duration for future sessions if provided
  safeStorageSet(URGENCY_TIMER_DURATION_KEY, String(parsedDuration));
  window[URGENCY_TIMER_MEMORY_DURATION_KEY] = parsedDuration;
  return candidate;
}

function restartUrgencySession(durationMs) {
  const parsedDuration = Number(durationMs) > 0
    ? Number(durationMs)
    : (Number(safeStorageGet(URGENCY_TIMER_DURATION_KEY)) || Number(window[URGENCY_TIMER_MEMORY_DURATION_KEY]) || 180000);
  const newEnd = nowMs() + parsedDuration;
  safeStorageSet(URGENCY_TIMER_STORAGE_KEY, String(newEnd));
  window[URGENCY_TIMER_MEMORY_END_KEY] = newEnd;
  window[URGENCY_TIMER_MEMORY_DURATION_KEY] = parsedDuration;
  return newEnd;
}

function formatTimeMMSS(msRemaining) {
  const total = Math.max(0, Math.floor(msRemaining / 1000));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  const mmStr = String(mm).padStart(2, '0');
  const ssStr = String(ss).padStart(2, '0');
  return `${mmStr}:${ssStr}`;
}

function initializeCartUrgencyTimer(rootEl) {
  if (!rootEl) return;
  // Cleanup if previously initialized
  if (rootEl._urgencyTimerCleanup && typeof rootEl._urgencyTimerCleanup === 'function') {
    try { rootEl._urgencyTimerCleanup(); } catch (e) {}
  }

  const durationMinutesAttr = rootEl.getAttribute('data-duration-minutes');
  const titleText = rootEl.getAttribute('data-title') || '⏰ Limited Time Offer';
  const copyText = rootEl.getAttribute('data-copy') || 'remaining to complete your order';
  const durationMs = Number(durationMinutesAttr) > 0 ? Number(durationMinutesAttr) * 60000 : 180000;

  let endAt = getOrStartUrgencyEndAt(durationMs);

  // Render initial structure using Tailwind utilities
  rootEl.innerHTML = `
    <div class="text-red-600 font-semibold text-sm leading-snug">${titleText}</div>
    <div class="text-sm leading-snug whitespace-nowrap timer-copy">
      <span>${copyText}</span>
      <span class="tabular-nums font-semibold" aria-label="time remaining" role="timer" data-clock>${formatTimeMMSS(endAt - nowMs())}</span>
    </div>
  `;

  const clockEl = rootEl.querySelector('[data-clock]');

  function tick() {
    if (!rootEl.isConnected) {
      cleanup();
      return;
    }
    const remaining = endAt - nowMs();
    if (remaining <= 0) {
      endAt = restartUrgencySession(durationMs);
    }
    if (clockEl) clockEl.textContent = formatTimeMMSS(endAt - nowMs());
  }

  const intervalId = window.setInterval(tick, 1000);
  // Immediate update to avoid 1s delay
  tick();

  function cleanup() {
    try { window.clearInterval(intervalId); } catch (e) {}
    rootEl._urgencyTimerCleanup = null;
  }

  rootEl._urgencyTimerCleanup = cleanup;
}

function initCartUrgencyTimersIn(container) {
  try {
    const scope = container || document;
    const timers = scope.querySelectorAll('.cart-urgency-timer');
    if (!timers || !timers.length) return;
    timers.forEach((el) => initializeCartUrgencyTimer(el));
  } catch (e) {}
}

// -----------------------------
// "People checking out" widget
// -----------------------------
const CART_ACTIVITY_INTERVAL_MS = 15000; // 15s
const CART_ACTIVITY_STATE_KEY = '__tecCartActivityState';

function clamp(n, min, max) { return Math.min(max, Math.max(min, n)); }

function getSessionSeed() {
  try {
    const key = 'tec:cartActivitySeed';
    const existing = window.sessionStorage.getItem(key);
    if (existing) return Number(existing) || Date.now();
    const seed = Date.now();
    window.sessionStorage.setItem(key, String(seed));
    return seed;
  } catch (e) {
    return Date.now();
  }
}

function mulberry32(a) {
  return function() {
    let t = (a += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function getCartActivityState() {
  if (!window[CART_ACTIVITY_STATE_KEY]) {
    window[CART_ACTIVITY_STATE_KEY] = {
      prev: null,
      noise: 1.0,
      prng: mulberry32(getSessionSeed())
    };
  }
  return window[CART_ACTIVITY_STATE_KEY];
}

function hourWeightsForBlackBox() {
  // Sum ~= 1.0; Thursday-like shape with strong 9–11 AM peak, noon dip, evening shelf
  return [
    0.009, 0.008, 0.006, 0.006, 0.007, 0.010, 0.017, 0.030,
    0.048, 0.110, 0.165, 0.095, 0.045, 0.040, 0.045, 0.040,
    0.042, 0.045, 0.050, 0.050, 0.050, 0.039, 0.025, 0.018
  ];
}

function computeCartActivityNow() {
  const state = getCartActivityState();
  const now = new Date();
  // Use store time: GMT-5 (fixed, no DST)
  const utcHour = now.getUTCHours();
  const STORE_GMT_OFFSET_HOURS = -5; // GMT-5
  const h = (utcHour + 24 + STORE_GMT_OFFSET_HOURS) % 24;
  const W = hourWeightsForBlackBox();
  // Black-box constants
  const ORDERS_PER_DAY = 900; // baseline
  const AVG_CHECKOUT_MIN = 7;
  const SCALE = 22; // visibility multiplier
  const MIN_DISPLAY = 40;
  const MAX_DISPLAY = 650;

  const expectedPerMinute = (ORDERS_PER_DAY * W[h]) / 60; // orders/minute in this hour
  // Mean-reverting random walk around 1.0 with gentle step; clamp to [0.9, 1.1]
  const rnd = (state.prng ? state.prng() : Math.random());
  // Slightly larger step to ensure visible movement
  state.noise += (rnd - 0.5) * 0.04 + (1 - state.noise) * 0.01;
  const R = clamp(state.noise, 0.9, 1.1);

  let next = Math.round(expectedPerMinute * AVG_CHECKOUT_MIN * SCALE * R);
  next = clamp(next, MIN_DISPLAY, MAX_DISPLAY);

  const MAX_DELTA = 5;
  if (state.prev != null) {
    next = state.prev + clamp(next - state.prev, -MAX_DELTA, MAX_DELTA);
  }
  // Nudge if stuck: if unchanged for several ticks, move by ±1 within bounds
  state.ticks = (state.ticks || 0) + 1;
  if (state.prev != null && next === state.prev) {
    state.stable = (state.stable || 0) + 1;
    if (state.stable >= 3) {
      const dir = state.ticks % 2 === 0 ? 1 : -1;
      next = clamp(state.prev + dir, MIN_DISPLAY, MAX_DISPLAY);
      state.stable = 0;
    }
  } else {
    state.stable = 0;
  }
  state.prev = next;
  return next;
}

function renderCartActivityContainer(rootEl) {
  if (!rootEl) return null;
  // Render structure once
  rootEl.innerHTML = `
    <div class="bg-[#EFE3DD] text-[#3b332f] rounded-md px-3 py-2 flex items-center gap-0.5 flex-row justify-center mb-4" aria-live="polite" role="status">
      <span class="inline-block w-2.5 h-2.5 rounded-full bg-[#2da64a] tec-pulse-dot"></span>
      <span><span data-activity-count class="tabular-nums font-semibold"></span> people checking out now</span>
    </div>
  `;
  const countEl = rootEl.querySelector('[data-activity-count]');

  function tick() {
    if (!rootEl.isConnected) {
      cleanup();
      return;
    }
    try {
      const n = computeCartActivityNow();
      if (countEl) countEl.textContent = String(n);
    } catch (e) {}
  }

  const intervalId = window.setInterval(tick, CART_ACTIVITY_INTERVAL_MS);
  // Immediate paint
  tick();

  function cleanup() {
    try { window.clearInterval(intervalId); } catch (e) {}
    rootEl._cartActivityCleanup = null;
  }
  rootEl._cartActivityCleanup = cleanup;
  return cleanup;
}

function initCartActivityIn(container) {
  try {
    const scope = container || document;
    const nodes = scope.querySelectorAll('[data-cart-activity]');
    if (!nodes || !nodes.length) return;
    nodes.forEach((el) => {
      if (el._cartActivityCleanup && typeof el._cartActivityCleanup === 'function') {
        try { el._cartActivityCleanup(); } catch (e) {}
      }
      renderCartActivityContainer(el);
    });
  } catch (e) {}
}

// Initialize on page load as a fallback
try {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { try { initCartActivityIn(document); } catch (e) {} });
  } else {
    initCartActivityIn(document);
  }
} catch (e) {}

// Expose minimal global API for business rule overrides
window.TECUrgencyTimer = window.TECUrgencyTimer || {
  reset: function(durationMinutes) {
    const durationMs = Number(durationMinutes) > 0 ? Number(durationMinutes) * 60000 : null;
    if (durationMs) safeStorageSet(URGENCY_TIMER_DURATION_KEY, String(durationMs));
    restartUrgencySession(durationMs || Number(safeStorageGet(URGENCY_TIMER_DURATION_KEY)) || 180000);
    initCartUrgencyTimersIn(document);
  },
  getEndAt: function() { return Number(safeStorageGet(URGENCY_TIMER_STORAGE_KEY)) || 0; }
};

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', 
    class CartDrawer extends HTMLElement {
      constructor() {
        super();
    
        this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.setHeaderCartIconAccessibility();
      }
    
      setHeaderCartIconAccessibility() {
        const cartLinks = document.querySelectorAll('.cart-icon-bubble');
        if (!cartLinks) return;

        cartLinks.forEach(cartLink => {
          cartLink.setAttribute('role', 'button');
          cartLink.setAttribute('aria-haspopup', 'dialog');
          cartLink.addEventListener('click', (event) => {
            event.preventDefault();
            this.open(cartLink);
          });
          cartLink.addEventListener('keydown', (event) => {
            if (event.code.toUpperCase() === 'SPACE') {
              event.preventDefault();
              this.open(cartLink);
            }
          });
        })
      }
    
      open(triggeredBy) {
        // Close any open nav drawers first
        const openNavDrawers = document.querySelectorAll('nav-drawer.active');
        openNavDrawers.forEach(navDrawer => {
          navDrawer.close();
        });

        if (triggeredBy) this.setActiveElement(triggeredBy);
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
        // here the animation doesn't seem to always get triggered. A timeout seem to help
        setTimeout(() => {
          this.classList.add('animate', 'active');
          // Upsell impressions key off the drawer actually opening, not off element connect.
          // The drawer markup exists on every page, so connect-time firing counted page views
          // as impressions and raced the Intelligems cell flag.
          document.dispatchEvent(new CustomEvent('tec:cart-drawer-open'));
        });

        this.addEventListener(
          'transitionend',
          () => {
            const containerToTrapFocusOn = this.classList.contains('is-empty')
              ? this.querySelector('.drawer__inner-empty')
              : document.getElementById('CartDrawer');
            const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
            trapFocus(containerToTrapFocusOn, focusElement);
          },
          { once: true }
        );
    
        document.body.classList.add('overflow-hidden');

        // Initialize widgets on open
        try { initCartUrgencyTimersIn(this); } catch (e) {}
        try { initCartActivityIn(this); } catch (e) {}
      }
    
      close() {
        this.classList.remove('active');
        removeTrapFocus(this.activeElement);
        document.body.classList.remove('overflow-hidden');
      }
    
      setSummaryAccessibility(cartDrawerNote) {
        cartDrawerNote.setAttribute('role', 'button');
        cartDrawerNote.setAttribute('aria-expanded', 'false');
    
        if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
          cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
        }
    
        cartDrawerNote.addEventListener('click', (event) => {
          event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
        });
    
        cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
      }
    
      renderContents(parsedState) {
        this.querySelector('.drawer__inner').classList.contains('is-empty') &&
          this.querySelector('.drawer__inner').classList.remove('is-empty');
        this.productId = parsedState.id;

        // Add Klaviyo tracking
        
        // Fetch current cart state for consistent data structure
        fetch(window.Shopify.routes.root + 'cart.js')
          .then(res => res.json())
          .then(cart => {
            
            if (!cart.items || !Array.isArray(cart.items)) {
              return;
            }

            const hasSubscription = cart.items.some(item => item?.selling_plan_allocation != null);
            
            const cartItems = cart.items.map(item => ({
              product_id: item.product_id,
              variant_id: item.variant_id,
              title: item.title,
              quantity: item.quantity,
              price: item.price/100,
              image: item.featured_image?.url || item.image || item.product.featured_image?.url || ''
            }));

            if (hasSubscription) {
              const cartKlaviyo = {
                total_price: cart.total_price/100,
                $value: cart.total_price/100,
                total_discount: cart.total_discount/100,
                original_total_price: cart.original_total_price/100,
                items: cartItems,
                subscriptionInCartDetected: true,
                $extra: {
                  subscriptionInCartDetected: true
                }
              };
              
              if (typeof _learnq !== "undefined") {
                _learnq.push(['track', 'Added to Cart', cartKlaviyo]);
              }

              // Set cart attribute for subscription
              // fetch('/cart/update.js', {
              //   method: 'POST',
              //   headers: {
              //     'Content-Type': 'application/json'
              //   },
              //   body: JSON.stringify({
              //     attributes: {
              //       _subInCart: true
              //     }
              //   })
              // });
            } else {
              const cartKlaviyo = {
                total_price: cart.total_price/100,
                $value: cart.total_price/100,
                total_discount: cart.total_discount,
                original_total_price: cart.original_total_price/100,
                items: cartItems,
                subscriptionInCartDetected: false,
                $extra: {
                  subscriptionInCartDetected: false
                }
              };
              
              if (typeof _learnq !== "undefined") {
                _learnq.push(['track', 'Added to Cart', cartKlaviyo]);
              }

              // Set cart attribute for subscription
              // fetch('/cart/update.js', {
              //   method: 'POST',
              //   headers: {
              //     'Content-Type': 'application/json'
              //   },
              //   body: JSON.stringify({
              //     attributes: {
              //       _subInCart: false
              //     }
              //   })
              // });
            }
          })
          .catch(error => console.error('Error fetching cart for Klaviyo:', error));

        this.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
            
          if (!sectionElement) return;
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        });
    
        setTimeout(() => {
          this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
          this.open();
          try { initCartUrgencyTimersIn(this); } catch (e) {}
          try { initCartActivityIn(this); } catch (e) {}
        });
      }
    
      getSectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      }
    
      getSectionsToRender() {
        return [
          {
            id: 'cart-drawer',
            selector: '#CartDrawer'
          },
          {
            id: 'cart-icon-bubble',
            selector: '#cart-icon-bubble--mobile'
          },
          {
            id: 'cart-icon-bubble',
            selector: '#cart-icon-bubble--desktop'
          }
        ];
      }
    
      getSectionDOM(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      }
    
      setActiveElement(element) {
        this.activeElement = element;
      }
    }
  );
}

if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', 
    class CartRemoveButton extends HTMLElement {
      constructor() {
        super();
    
        this.addEventListener('click', async (event) => {
          event.preventDefault();
          const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
          const itemEl = this.closest('.cart-item');
          if (!cartItems || !itemEl) {
            return;
          }
          const removeIndex = this.dataset.index;
          const variantInput = itemEl.querySelector('input.quantity__input');
          const variantId = variantInput ? variantInput.dataset.quantityVariantId : undefined;
          console.log('[cart-remove-button] click', { removeIndex, variantId });
          // If this is the carbon offset item being removed, update localStorage
          if (variantId === '42280675836083') {
            localStorage.setItem('carbonOffset', 'no');
          }

          const role = itemEl.getAttribute('data-bundle-role');
          const group = itemEl.getAttribute('data-bundle-group');
          const handle = itemEl.getAttribute('data-bundle-handle');
          console.log('[cart-remove-button] bundle meta', { role, group, handle });

          // Helper to post change without re-rendering sections immediately
          const postChangeNoRender = (line, quantity) => {
            const body = JSON.stringify({ line, quantity });
            return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
              .then((r) => r.text())
              .catch(() => {});
          };

          // If not a bundle or missing group metadata, fallback to single-line removal
          if (!group || !role) {
            console.log('[cart-remove-button] no bundle group/role, removing single line');
            cartItems.updateQuantity(removeIndex, 0);
            return;
          }

          // Collect targeted lines to remove using current DOM to ensure correct 1-based indices
          const container = cartItems;
          const allCartItemEls = Array.from(container.querySelectorAll('.cart-item'));
          console.log('[cart-remove-button] cart items in DOM', allCartItemEls.map(el => ({
            id: el.id,
            group: el.getAttribute('data-bundle-group'),
            role: el.getAttribute('data-bundle-role')
          })));

          let targetLines = [];
          if (role === 'main') {
            targetLines = allCartItemEls
              .filter(el => el.getAttribute('data-bundle-group') === group)
              .map(el => {
                // Prefer the input data-index; fallback to id suffix
                const input = el.querySelector('input.quantity__input');
                const idx = input && input.getAttribute('data-index');
                if (idx) return idx;
                const m = (el.id || '').match(/Cart(?:Drawer-)?Item-(\d+)/);
                return m ? m[1] : null;
              })
              .filter(Boolean);
          } else {
            // role === 'child': remove only this specific child item
            targetLines = [removeIndex];
          }
          console.log('[cart-remove-button] target lines before sort', targetLines);

          if (!targetLines.length) {
            console.warn('[cart-remove-button] no target lines found, fallback to single removal', { removeIndex });
            cartItems.updateQuantity(removeIndex, 0);
            return;
          }

          // Show loading using the first targeted line
          cartItems.enableLoading(String(targetLines[0]));

          // Remove all targeted lines sequentially without re-render (sort desc to avoid index shifts)
          const linesDesc = targetLines
            .map((n) => Number(n))
            .filter((n) => !Number.isNaN(n))
            .sort((a, b) => b - a)
            .map((n) => String(n));
          console.log('[cart-remove-button] removing lines (desc)', linesDesc);

          for (const ln of linesDesc) {
            try { await postChangeNoRender(String(ln), 0); } catch (e) {}
          }

          // Single refresh of drawer and footer
          try {
            console.log('[cart-remove-button] refreshing cart drawer sections');
            if (cartItems.tagName === 'CART-DRAWER-ITEMS') {
              const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
              const responseText = await response.text();
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              // Replace entire CartDrawer inner to ensure empty state sync
              const sourceCartDrawer = html.querySelector('#CartDrawer');
              const targetCartDrawer = document.getElementById('CartDrawer');
              if (sourceCartDrawer && targetCartDrawer) {
                targetCartDrawer.replaceWith(sourceCartDrawer);
                try {
                  const cd = document.querySelector('cart-drawer');
                  cd.querySelector('#CartDrawer-Overlay').addEventListener('click', cd.close.bind(cd));
                } catch (e) {}
              } else {
                const selectors = ['cart-drawer-items', '.drawer__footer'];
                for (const selector of selectors) {
                  const targetElement = document.querySelector(selector);
                  const sourceElement = html.querySelector(selector);
                  if (targetElement && sourceElement) {
                    targetElement.replaceWith(sourceElement);
                  }
                }
              }
              // Sync wrapper empty state
              try {
                const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
                const cart = await cartRes.json();
                const cartDrawerWrapper = document.querySelector('cart-drawer');
                if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', (cart?.item_count || 0) === 0);
              } catch (e) {}
              try { initCartUrgencyTimersIn(document); } catch (e) {}
            } else {
              // Main cart page
              const response = await fetch(`${routes.cart_url}?section_id=main-cart-items`);
              const responseText = await response.text();
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector('cart-items');
              if (sourceQty) cartItems.innerHTML = sourceQty.innerHTML;
            }
          } catch (e) {
            console.error(e);
          } finally {
            cartItems.disableLoading(String(targetLines[0]));
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
          }
        });
      }
    }
  );
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    if (event.target.closest("quantity-popover")) {
      this.validateQuantity(event);
      return;
    }
    if (event.target.closest(".cart-item-selling-plan")) {
      this.handleSellingPLans(event);
    }
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          // Ensure wrapper empty state is in sync
          try {
            fetch(window.Shopify.routes.root + 'cart.js')
              .then(res => res.json())
              .then(cart => {
                const cartDrawerWrapper = document.querySelector('cart-drawer');
                if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', (cart?.item_count || 0) === 0);
              });
          } catch (e) {}
          try { initCartUrgencyTimersIn(document); } catch (e) {}
          try { initCartActivityIn(document); } catch (e) {}
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    
    return [
      {
        id: 'main-cart-items',
        section: mainCartItems?.dataset?.id || null,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: mainCartFooter?.dataset?.id || null,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId, selling_plan = null) {
    this.enableLoading(line);

    // Grouped removal when quantity set to 0 via quantity input/buttons
    try {
      if (Number(quantity) === 0) {
        const itemEl = document.getElementById(`CartDrawer-Item-${line}`) || document.getElementById(`CartItem-${line}`);
        if (itemEl) {
          const role = itemEl.getAttribute('data-bundle-role');
          const group = itemEl.getAttribute('data-bundle-group');
          const handle = itemEl.getAttribute('data-bundle-handle');
          console.log('[cart-items:updateQuantity] qty=0 detected', { line, role, group, handle });

          // Update carbon offset preference if needed
          try {
            const vin = itemEl.querySelector('input.quantity__input');
            if (vin && vin.dataset.quantityVariantId === '42280675836083') {
              localStorage.setItem('carbonOffset', 'no');
            }
          } catch (e) {}

          // Only handle bundle removal if both group and role exist
          if (group && role) {
            const container = this;
            const allCartItemEls = Array.from(container.querySelectorAll('.cart-item'));
            let targetLines = [];
            if (role === 'main') {
              targetLines = allCartItemEls
                .filter(el => el.getAttribute('data-bundle-group') === group)
                .map(el => {
                  const input = el.querySelector('input.quantity__input');
                  const idx = input && input.getAttribute('data-index');
                  if (idx) return idx;
                  const m = (el.id || '').match(/Cart(?:Drawer-)?Item-(\d+)/);
                  return m ? m[1] : null;
                })
                .filter(Boolean);
            } else {
              // role === 'child': remove only this specific child item
              targetLines = [String(line)];
            }
            console.log('[cart-items:updateQuantity] target lines before sort', targetLines);

            if (targetLines.length) {
              const postChangeNoRender = (ln, qty) => {
                const body = JSON.stringify({ line: ln, quantity: qty });
                return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
                  .then((r) => r.text())
                  .catch(() => {});
              };

              const linesDesc = targetLines
                .map((n) => Number(n))
                .filter((n) => !Number.isNaN(n))
                .sort((a, b) => b - a)
                .map((n) => String(n));
              console.log('[cart-items:updateQuantity] removing lines (desc)', linesDesc);

              (async () => {
                for (const ln of linesDesc) {
                  try { await postChangeNoRender(String(ln), 0); } catch (e) {}
                }
                try {
                  console.log('[cart-items:updateQuantity] refreshing sections after grouped removal');
                  if (this.tagName === 'CART-DRAWER-ITEMS') {
                    const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
                    const responseText = await response.text();
                    const html = new DOMParser().parseFromString(responseText, 'text/html');
                    const sourceCartDrawer = html.querySelector('#CartDrawer');
                    const targetCartDrawer = document.getElementById('CartDrawer');
                    if (sourceCartDrawer && targetCartDrawer) {
                      targetCartDrawer.replaceWith(sourceCartDrawer);
                      try {
                        const cd = document.querySelector('cart-drawer');
                        cd.querySelector('#CartDrawer-Overlay').addEventListener('click', cd.close.bind(cd));
                      } catch (e) {}
                    } else {
                      const selectors = ['cart-drawer-items', '.drawer__footer'];
                      for (const selector of selectors) {
                        const targetElement = document.querySelector(selector);
                        const sourceElement = html.querySelector(selector);
                        if (targetElement && sourceElement) {
                          targetElement.replaceWith(sourceElement);
                        }
                      }
                    }
                    // Sync wrapper empty state
                    try {
                      const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
                      const cart = await cartRes.json();
                      const cartDrawerWrapper = document.querySelector('cart-drawer');
                      if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', (cart?.item_count || 0) === 0);
                    } catch (e) {}
                    try { initCartUrgencyTimersIn(document); } catch (e) {}
                  } else {
                    const response = await fetch(`${routes.cart_url}?section_id=main-cart-items`);
                    const responseText = await response.text();
                    const html = new DOMParser().parseFromString(responseText, 'text/html');
                    const sourceQty = html.querySelector('cart-items');
                    if (sourceQty) this.innerHTML = sourceQty.innerHTML;
                  }
                } catch (e) {
                  console.error(e);
                } finally {
                  this.disableLoading(line);
                  publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
                }
              })();

              return; // prevent default single-line update
            }
          }
        }
      }
    } catch (e) {
      console.error('[cart-items:updateQuantity] grouped removal error', e);
    }

    // For quantity 0 (removal), use simpler format without sections to avoid 422 errors
    // Then refresh sections separately after successful removal
    const isRemoval = Number(quantity) === 0;
    
    let body;
    if (isRemoval) {
      // Simple removal request - same format as bundle removal
      body = JSON.stringify({
        line: Number(line),
        quantity: 0
      });
      console.log('[cart-items] updateQuantity removal request:', { line: Number(line), quantity: 0 });
    } else {
      // Regular update with sections
      const sectionsToRender = this.getSectionsToRender()
        .map((section) => section.section)
        .filter(section => section !== undefined && section !== null);

      body = JSON.stringify({
        line: Number(line),
        quantity: Number(quantity),
        ...(selling_plan && { selling_plan }),
        ...(sectionsToRender.length > 0 && { sections: sectionsToRender }),
        sections_url: window.location.pathname,
      });
      console.log('[cart-items] updateQuantity update request:', { line: Number(line), quantity: Number(quantity), selling_plan, sections: sectionsToRender });
    }

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        if (!response.ok) {
          console.error('[cart-items] updateQuantity response not ok:', response.status, response.statusText);
          // Try to get error details from response
          return response.text().then(text => {
            let errorData;
            try {
              errorData = JSON.parse(text);
              console.error('[cart-items] updateQuantity error details:', errorData);
            } catch (e) {
              console.error('[cart-items] updateQuantity error response text:', text);
            }
            throw new Error(`Cart update failed: ${response.status} ${response.statusText}`);
          });
        }
        return response.text();
      })
      .then((state) => {
        let parsedState;
        try {
          parsedState = JSON.parse(state);
        } catch (e) {
          console.error('[cart-items] updateQuantity JSON parse error:', e, state);
          this.disableLoading(line);
          this.updateLiveRegions(line, window.cartStrings?.error || 'Unable to update cart. Please try again.');
          return;
        }
        
        // If this was a removal (quantity=0) and we didn't include sections in the request,
        // we need to refresh the cart drawer sections manually
        if (isRemoval && (!parsedState.sections || Object.keys(parsedState.sections || {}).length === 0)) {
          console.log('[cart-items] Removal successful, refreshing sections...');
          // Refresh cart drawer sections
          if (this.tagName === 'CART-DRAWER-ITEMS') {
            fetch(`${routes.cart_url}?section_id=cart-drawer`)
              .then((response) => response.text())
              .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');
                const sourceCartDrawer = html.querySelector('#CartDrawer');
                const targetCartDrawer = document.getElementById('CartDrawer');
                if (sourceCartDrawer && targetCartDrawer) {
                  targetCartDrawer.replaceWith(sourceCartDrawer);
                  try {
                    const cd = document.querySelector('cart-drawer');
                    if (cd) {
                      cd.querySelector('#CartDrawer-Overlay')?.addEventListener('click', cd.close.bind(cd));
                    }
                  } catch (e) {}
                } else {
                  const selectors = ['cart-drawer-items', '.drawer__footer'];
                  for (const selector of selectors) {
                    const targetElement = document.querySelector(selector);
                    const sourceElement = html.querySelector(selector);
                    if (targetElement && sourceElement) {
                      targetElement.replaceWith(sourceElement);
                    }
                  }
                }
                // Sync wrapper empty state
                return fetch(window.Shopify.routes.root + 'cart.js')
                  .then(res => res.json())
                  .then(cart => {
                    const cartDrawerWrapper = document.querySelector('cart-drawer');
                    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', (cart?.item_count || 0) === 0);
                  })
                  .catch(e => {});
              })
              .then(() => {
                try { initCartUrgencyTimersIn(document); } catch (e) {}
                try { initCartActivityIn(document); } catch (e) {}
                this.disableLoading(line);
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
              })
              .catch((e) => {
                console.error('[cart-items] Error refreshing sections after removal:', e);
                this.disableLoading(line);
              });
            return; // Exit early, sections refresh will handle the rest
          } else {
            // Main cart page
            fetch(`${routes.cart_url}?section_id=main-cart-items`)
              .then((response) => response.text())
              .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');
                const sourceQty = html.querySelector('cart-items');
                if (sourceQty) this.innerHTML = sourceQty.innerHTML;
                this.disableLoading(line);
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
              })
              .catch((e) => {
                console.error('[cart-items] Error refreshing sections after removal:', e);
                this.disableLoading(line);
              });
            return; // Exit early, sections refresh will handle the rest
          }
        }
        
        // Regular flow for non-removal or when sections are included
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          // Reset quantity input if it exists
          if (quantityElement) {
            const originalValue = quantityElement.getAttribute('value');
            if (originalValue !== null) {
              quantityElement.value = originalValue;
            }
          }
          // Format error message - handle different error formats from Shopify
          let errorMessage = '';
          if (typeof parsedState.errors === 'string') {
            errorMessage = parsedState.errors;
          } else if (Array.isArray(parsedState.errors)) {
            errorMessage = parsedState.errors.join(', ');
          } else if (typeof parsedState.errors === 'object') {
            // Try common error properties
            errorMessage = parsedState.errors.message || 
                          parsedState.errors.description || 
                          parsedState.errors.cart || 
                          Object.values(parsedState.errors)[0] || 
                          window.cartStrings?.error || 
                          'Cart Error';
            // If still an array/object, stringify it
            if (typeof errorMessage !== 'string') {
              errorMessage = Array.isArray(errorMessage) ? errorMessage.join(', ') : JSON.stringify(errorMessage);
            }
          } else {
            errorMessage = window.cartStrings?.error || 'Cart Error';
          }
          this.updateLiveRegions(line, errorMessage);
          this.disableLoading(line);
          return;
        }

        // Add Klaviyo tracking
        
        // Fetch current cart state for consistent data structure
        fetch(window.Shopify.routes.root + 'cart.js')
          .then(res => res.json())
          .then(cart => {
            
            if (!cart.items || !Array.isArray(cart.items)) {
              return;
            }

            const hasSubscription = cart.items.some(item => item?.selling_plan_allocation != null);
            
            const cartItems = cart.items.map(item => ({
              product_id: item.product_id,
              variant_id: item.variant_id,
              title: item.title,
              quantity: item.quantity,
              price: item.price/100,
              image: item.featured_image?.url || item.image || item.product.featured_image?.url || ''
            }));

            if (hasSubscription) {
              const cartKlaviyo = {
                total_price: cart.total_price/100,
                $value: cart.total_price/100,
                total_discount: cart.total_discount,
                original_total_price: cart.original_total_price/100,
                items: cartItems,
                subscriptionInCartDetected: true,
                $extra: {
                  subscriptionInCartDetected: true
                }
              };
              
              if (typeof _learnq !== "undefined") {
                _learnq.push(['track', 'Added to Cart', cartKlaviyo]);
              }

              // Set cart attribute for subscription
              // fetch('/cart/update.js', {
              //   method: 'POST',
              //   headers: {
              //     'Content-Type': 'application/json'
              //   },
              //   body: JSON.stringify({
              //     attributes: {
              //       _subInCart: true
              //     }
              //   })
              // });
            } else {
              const cartKlaviyo = {
                total_price: cart.total_price/100,
                $value: cart.total_price/100,
                total_discount: cart.total_discount,
                original_total_price: cart.original_total_price/100,
                items: cartItems,
                subscriptionInCartDetected: false,
                $extra: {
                  subscriptionInCartDetected: false
                }
              };
              
              if (typeof _learnq !== "undefined") {
                _learnq.push(['track', 'Added to Cart', cartKlaviyo]);
              }

              // Set cart attribute for subscription
              // fetch('/cart/update.js', {
              //   method: 'POST',
              //   headers: {
              //     'Content-Type': 'application/json'
              //   },
              //   body: JSON.stringify({
              //     attributes: {
              //       _subInCart: false
              //     }
              //   })
              // });
            }
          })
          .catch(error => console.error('Error fetching cart for Klaviyo:', error));

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
 
        // Render sections - ensure sections exist in response
        if (parsedState.sections) {
          this.getSectionsToRender().forEach((section) => {
            const sectionId = section.section || section.id;
            if (parsedState.sections[sectionId]) {
              try {
                const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
                if (elementToReplace) {
                  elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[sectionId], section.selector);
                }
              } catch (e) {
                console.error('[cart-items] Error rendering section:', section.id, e);
              }
            }
          });
        }
        try { initCartUrgencyTimersIn(document); } catch (e) {}

        // Check if item still exists (might be removed if quantity was 0)
        const updatedValue = parsedState.items && parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        // Only check quantity mismatch if quantityElement exists and item wasn't removed
        if (quantityElement && items.length === parsedState.items.length && updatedValue !== undefined) {
          const currentValue = parseInt(quantityElement.value);
          if (!isNaN(currentValue) && updatedValue !== currentValue) {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((error) => {
        console.error('[cart-items] updateQuantity error:', error);
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        
        // Show error in line item error element
        let errorMessage = window.cartStrings?.error || 'Unable to update cart. Please try again.';
        
        // Try to extract error message from error object
        if (error.message) {
          errorMessage = error.message;
        } else if (error.errors) {
          if (typeof error.errors === 'string') {
            errorMessage = error.errors;
          } else if (Array.isArray(error.errors)) {
            errorMessage = error.errors.join(', ');
          } else if (typeof error.errors === 'object') {
            errorMessage = error.errors.message || error.errors.description || Object.values(error.errors)[0] || errorMessage;
          }
        }
        
        this.updateLiveRegions(line, errorMessage);
        
        // Also show in general cart errors if available
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) {
          errors.textContent = errorMessage;
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorTextElement = lineItemError.querySelector('.cart-item__error-text');
      if (errorTextElement) {
        // Set error message - CSS :has() selector will handle visibility automatically
        errorTextElement.textContent = (message && message.trim()) ? message : '';
      }
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', true);
    }

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    console.log
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  handleSellingPLans(event) {

    const quantity = parseInt(event.target.dataset.quantity);
    const index = event.target.dataset.index;
    let sellingPlanID;
    if (event.target.tagName == "INPUT" && !event.target.checked) {
      sellingPlanID = null;
    } else if (event.target.tagName == "SELECT") {
      sellingPlanID = event.target.value;
    } else {
      return;
    }
    
    this.updateQuantity(
      index,
      quantity,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId,
      sellingPlanID
    );
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', 
    class CartDrawerItems extends CartItems {
      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble--mobile',
            section: 'cart-icon-bubble',
            selector: '#cart-icon-bubble--mobile',
          },
          {
            id: 'cart-icon-bubble--desktop',
            section: 'cart-icon-bubble',
            selector: '#cart-icon-bubble--mobile',
          },
        ];
      }
    }
  );
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', 
    class QuantityInput extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));
        this.querySelectorAll('button').forEach((button) =>
          button.addEventListener('click', this.onButtonClick.bind(this))
        );
      }
    
      quantityUpdateUnsubscriber = undefined;
    
      connectedCallback() {
        this.validateQtyRules();
        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
      }
    
      disconnectedCallback() {
        if (this.quantityUpdateUnsubscriber) {
          this.quantityUpdateUnsubscriber();
        }
      }
    
      onInputChange(event) {
        this.validateQtyRules();
      }
    
      onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;
    
        if (event.target.name === 'plus') {
          if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
            this.input.value = this.input.dataset.min;
          } else {
            this.input.stepUp();
          }
        } else {
          this.input.stepDown();
        }

        // if the user is removing the carbon offset item, update localStorage
        if (
          event.target.name === 'minus' &&
          parseInt(previousValue) === 1 && // about to become 0
          this.input.dataset.quantityVariantId === '42280675836083'
        ) {
          localStorage.setItem('carbonOffset', 'no');
        }
    
        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    
        if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
          this.input.value = parseInt(this.input.min);
        }
      }
    
      validateQtyRules() {
        const value = parseInt(this.input.value);
        if (this.input.min) {
          const buttonMinus = this.querySelector(".quantity__button[name='minus']");
          buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
        }
        if (this.input.max) {
          const max = parseInt(this.input.max);
          const buttonPlus = this.querySelector(".quantity__button[name='plus']");
          buttonPlus.classList.toggle('disabled', value >= max);
        }
      }
    }
  );
}

if (!customElements.get('quantity-popover')) {
  customElements.define(
    'quantity-popover',
    class QuantityPopover extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 990px)');
        this.mqlTablet = window.matchMedia('(min-width: 750px)');
        this.infoButtonDesktop = this.querySelector('.quantity-popover__info-button--icon-only');
        this.infoButtonMobile = this.querySelector('.quantity-popover__info-button--icon-with-label');
        this.popoverInfo = this.querySelector('.quantity-popover__info');
        this.closeButton = this.querySelector('.button-close');
        this.eventMouseEnterHappened = false;

        if (this.closeButton) {
          this.closeButton.addEventListener('click', this.closePopover.bind(this));
        }

        if (this.popoverInfo && this.infoButtonDesktop && this.mqlTablet.matches) {
          this.popoverInfo.addEventListener('mouseleave', this.closePopover.bind(this));
        }

        if (this.infoButtonDesktop) {
          this.infoButtonDesktop.addEventListener('click', this.togglePopover.bind(this));
          this.infoButtonDesktop.addEventListener('focusout', this.closePopover.bind(this));
        }

        if (this.infoButtonMobile) {
          this.infoButtonMobile.addEventListener('click', this.togglePopover.bind(this));
        }

        if (this.infoButtonDesktop && this.mqlTablet.matches) {
          this.infoButtonDesktop.addEventListener('mouseenter', this.togglePopover.bind(this));
          this.infoButtonDesktop.addEventListener('mouseleave', this.closePopover.bind(this));
        }
      }

      togglePopover(event) {
        event.preventDefault();
        if (event.type === 'mouseenter') {
          this.eventMouseEnterHappened = true;
        }

        if (event.type === 'click' && this.eventMouseEnterHappened) return;

        const button = this.infoButtonDesktop && this.mql.matches ? this.infoButtonDesktop : this.infoButtonMobile;
        const isExpanded = button.getAttribute('aria-expanded') === 'true';

        if ((this.mql.matches && !isExpanded) || event.type === 'click') {
          button.setAttribute('aria-expanded', !isExpanded);

          this.popoverInfo.toggleAttribute('hidden');

          button.classList.toggle('quantity-popover__info-button--open');

          this.infoButtonDesktop.classList.add('quantity-popover__info-button--icon-only--animation');
        }

        const isOpen = button.getAttribute('aria-expanded') === 'true';

        if (isOpen && event.type !== 'mouseenter') {
          button.focus();
          button.addEventListener('keyup', (e) => {
            if (e.key === 'Escape') {
              this.closePopover(e);
            }
          });
        }
      }

      closePopover(event) {
        event.preventDefault();
        const isButtonChild = this.infoButtonDesktop.contains(event.relatedTarget);
        const isPopoverChild = this.popoverInfo.contains(event.relatedTarget);

        const button = this.infoButtonDesktop && this.mql.matches ? this.infoButtonDesktop : this.infoButtonMobile;

        if (!isButtonChild && !isPopoverChild) {
          button.setAttribute('aria-expanded', 'false');
          button.classList.remove('quantity-popover__info-button--open');
          this.popoverInfo.setAttribute('hidden', '');
          this.infoButtonDesktop.classList.remove('quantity-popover__info-button--icon-only--animation');
        }

        this.eventMouseEnterHappened = false;
      }
    }
  );
}

if (!customElements.get('carbon-offset')) {
  customElements.define(
    'carbon-offset',
    class CarbonOffset extends HTMLElement {
      constructor() {
        super();
        this.onChangeListener = this.onChange.bind(this);
        this.carbonOffsetVariantID = 42280675836083;
        this.cart = document.querySelector('cart-drawer');
        this.checkbox = this.querySelector("label");
        
        // Initialize checkbox state from localStorage
        const carbonOffsetPreference = localStorage.getItem('carbonOffset');
        if (carbonOffsetPreference === 'no') {
          this.checkbox.previousElementSibling.checked = false;
        }
      }

      connectedCallback() {
        this.addEventListener('change', this.onChangeListener);
      }

      getFormData(addEvent) {
        let formData;
        if (addEvent) {
          formData = {
            items: [
              {
                id: this.carbonOffsetVariantID,
                quantity: 1,
                selling_plan: this.dataset.plan ? this.dataset.plan : null
              }
            ]
          }
        } else {
          formData = {
            updates: {
              [this.carbonOffsetVariantID]: 0
            }
          }
        }

        if (this.cart) {
          formData.sections = this.cart.getSectionsToRender().map((section) => section.id);
          formData.sections_url = window.location.pathname
          this.cart.setActiveElement(document.activeElement);
        }

        return formData
      }

      getCartConfig(addEvent) {
        const config = fetchConfig();
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(this.getFormData(addEvent))
        return config
      }

      toggleLoadingState(loading = false) {
        if (loading) {
          this.checkbox.classList.add("loading");
        } else {
          this.checkbox.classList.remove("loading");
        }
      }

      onChange(event) {
        this.toggleLoadingState(true);
        let config = this.getCartConfig(event.target.checked)
        let url = event.target.checked ? routes.cart_add_url : routes.cart_update_url

        // Update localStorage preference
        localStorage.setItem('carbonOffset', event.target.checked ? 'yes' : 'no');

        fetch(`${url}`, config)
        .then((response) => response.json())
        .then((response) => {
          
          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'carbon-offset',
            cartData: response,
          });

          this.cart.renderContents(response);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.toggleLoadingState();
        });
      }
    }
  );
}

if (!customElements.get('cart-upsells')) {
  customElements.define(
    'cart-upsells',
    class CartUpsells extends HTMLElement {
      constructor() {
        super();
        this.productsContainer = this.querySelector(".cart__upsells__products");
        this.excludedProducts = [
          '7882366451891','8401531928755','7495501611187','8145126588595','8273320738995','8284697788595', '8331700699315', '8331700666547', '8331700633779', '8331700601011'
        ];
      }

      connectedCallback() {
        if (this.dataset.cartEmpty === 'true') return;
        if (window.__TEC_UPSELL_CELL !== 'control') return; // v1 is the default now
        this.performRecommendations();
      }

      disconnectedCallback() {
        if (this.teardownImpression) this.teardownImpression();
      }

      async performRecommendations() {
        const complementaryUrl = this.dataset.url + "&intent=complementary";
        const response = await fetch(complementaryUrl);
        const text = await response.text();
        const recommendations = document.createRange().createContextualFragment(`${text}`).querySelector('.cart__upsells__products');

        if (recommendations?.innerHTML.trim().length) {
          this.productsContainer.innerHTML = '';
          Array.from(recommendations.children)
            .filter(el => !this.excludedProducts.includes(el.dataset.productid))
            .forEach(el => this.productsContainer.append(el));
          if (this.productsContainer.children.length > 0) {
            this.trackShown();
            this.classList.add("performed");
            return;
          }
        }

        const relatedUrl = this.dataset.url + "&intent=related";
        const relatedResponse = await fetch(relatedUrl);
        const relatedText = await relatedResponse.text();
        const relatedRecommendations = document.createRange().createContextualFragment(`${relatedText}`).querySelector('.cart__upsells__products');

        if (relatedRecommendations?.innerHTML.trim().length) {
          this.productsContainer.innerHTML = '';
          Array.from(relatedRecommendations.children)
            .filter(el => !this.excludedProducts.includes(el.dataset.productid))
            .forEach(el => this.productsContainer.append(el));
          if (this.productsContainer.children.length > 0) {
            this.trackShown();
            this.classList.add("performed");
          }
        }
      }

      trackShown() {
        if (typeof window.tecUpsellImpression !== 'function') return;
        if (typeof window.tecUpsellWhenDrawerOpen !== 'function') return;
        const shown = Array.from(this.productsContainer.children).map(
          (el) => el.dataset.productid || el.getAttribute('data-productId')
        );
        if (!shown.length) return;
        this.teardownImpression = window.tecUpsellWhenDrawerOpen(this, () => {
          // Re-checked at fire time: Intelligems may have landed since the fetch began, and
          // these tiles are CSS-hidden in the v1 cell, so they were never seen.
          if (window.__TEC_UPSELL_CELL !== 'control') return; // v1 is the default now
          window.tecUpsellImpression({
            // Stamped explicitly: this impression belongs to control by construction and
            // must never inherit a cell value that changed after the fetch started.
            cell: 'control',
            shown_type: 'product_id',
            seed_group: null,
            seed_product_id: this.dataset.product || null,
            shown: shown,
            cart_value: Number(this.dataset.cartValue || 0) / 100,
          });
        });
      }
    }
  );
}

if (!customElements.get('cart-upsell-product')) {
  customElements.define(
    'cart-upsell-product',
    class CartUpsellProduct extends HTMLElement {
      constructor() {
        super();
      }


      connectedCallback() {
        this.submitButton = this.querySelector(".cart-upsell-product__submit");
        this.cart = this.closest('cart-drawer');
        this.onClickListener = this.onClick.bind(this);
        this.submitButton.addEventListener('click', this.onClickListener );
      }

      getFormData() {
        let variantId = this.querySelector(".cart-upsell-product__variant-picker").value;

        const formData = {
          items: [
            {
              id: variantId,
              quantity: 1
            }
          ]
        }

        if (this.cart) {
          formData.sections = this.cart.getSectionsToRender().map((section) => section.id);
          formData.sections_url = window.location.pathname
          this.cart.setActiveElement(document.activeElement);
        }

        return formData
      }

      getCartConfig() {
        const config = fetchConfig();
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(this.getFormData());
        return config;
      }

      onClick(event) {
        event.preventDefault();
        this.submitButton.classList.add("loading");
        this.submitButton.querySelector(".loading__spinner").classList.remove("hidden");
        const variantId = this.querySelector('.cart-upsell-product__variant-picker').value;
        let config = this.getCartConfig();

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            // cart/add.js answers an error with a body carrying an HTTP-ish status. Adding
            // nothing is not a conversion, and renderContents would throw on that body.
            if (response && response.status && Number(response.status) >= 400) return;

            if (typeof window.tecUpsellTrack === 'function') {
              const seedEl = this.closest('cart-upsells');
              window.tecUpsellTrack('Added Cart Upsell', {
                cell: 'control',
                seed_group: null,
                seed_product_id: seedEl ? seedEl.dataset.product : null,
                upsold_group: null,
                upsold_product_id: this.dataset.productid || this.getAttribute('data-productId'),
                upsold_variant_id: variantId,
                combo_key: null,
                position: this.parentElement
                  ? Array.prototype.indexOf.call(this.parentElement.children, this) + 1
                  : null,
                cart_value: seedEl ? Number(seedEl.dataset.cartValue || 0) / 100 : null,
              });
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-upsell',
              cartData: response
            });

            this.cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
          });
      }

    }
  );
}

if (!customElements.get('cart-free-gift')) {
  customElements.define(
    'cart-free-gift',
    class CartFreeGift extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.submitButton = this.querySelector(".cart__free-gift__submit");
        this.cart = this.closest('cart-drawer');
        this.onClickListener = this.onClick.bind(this);
        this.submitButton.addEventListener('click', this.onClickListener );
      }

      getFormData() {
        const formData = {
          items: [
            {
              id: this.dataset.variantId,
              quantity: 1
            }
          ]
        }

        if (this.cart) {
          formData.sections = this.cart.getSectionsToRender().map((section) => section.id);
          formData.sections_url = window.location.pathname
          this.cart.setActiveElement(document.activeElement);
        }

        return formData
      }

      getCartConfig() {
        const config = fetchConfig();
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(this.getFormData());
        return config;
      }

      onClick(event) {
        event.preventDefault();
        this.submitButton.classList.add("loading");
        this.submitButton.querySelector(".loading__spinner").classList.remove("hidden");
        let config = this.getCartConfig();

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-free-gift',
              cartData: response
            });
            
            this.cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
          });
      }

    }
  );
}

if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLElement {
      constructor() {
        super();
        // Support both cart drawer and cart page
        this.input = this.querySelector('#CartDrawer-DiscountCode') || this.querySelector('#CartPage-DiscountCode');
        this.applyButton = this.querySelector('#CartDrawer-DiscountApply') || this.querySelector('#CartPage-DiscountApply');
        this.errorContainer = this.querySelector('#CartDrawer-DiscountError') || this.querySelector('#CartPage-DiscountError');
        this.successContainer = this.querySelector('#CartDrawer-DiscountSuccess') || this.querySelector('#CartPage-DiscountSuccess');
        this.cart = this.closest('cart-drawer');

        // Check if a discount was just applied (flag survives re-render)
        if (window.__discountJustApplied) {
          // Keep showing the message even after async re-renders settle
          const showMsg = () => {
            const el = document.getElementById('CartDrawer-DiscountSuccess') || document.getElementById('CartPage-DiscountSuccess');
            if (el) {
              el.textContent = 'Discount code applied';
              el.classList.remove('hidden');
            }
          };
          showMsg();
          // Re-apply after any async re-renders (Klaviyo fetch, etc.)
          const obs = new MutationObserver(() => showMsg());
          obs.observe(this, { childList: true, subtree: true });
          setTimeout(() => { obs.disconnect(); delete window.__discountJustApplied; }, 3000);
        }

        if (this.applyButton) {
          this.applyButton.addEventListener('click', this.handleApply.bind(this));
        }
        if (this.input) {
          this.input.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              this.handleApply();
            }
          });
        }

        // Sidecart only: "Have a promo code?" toggle expands the field row.
        // Cart page markup has no .cart__discount-toggle, so this is a no-op there.
        this.toggleBtn = this.querySelector('.cart__discount-toggle');
        if (this.toggleBtn) {
          this.toggleBtn.addEventListener('click', () => {
            this.setAttribute('data-expanded', 'true');
            this.toggleBtn.setAttribute('aria-expanded', 'true');
          });
        }
      }

      toggleLoadingState(loading = false) {
        if (loading) {
          if (this.applyButton) {
            this.applyButton.disabled = true;
            // Remove gray-out classes if they exist
            this.applyButton.classList.remove('opacity-50', 'cursor-not-allowed');
            
            // Store original button content
            if (!this._originalButtonContent) {
              this._originalButtonContent = this.applyButton.innerHTML;
            }
            
            // Hide all text content and show only spinner
            this.applyButton.innerHTML = `
              <svg class="animate-spin h-4 w-4 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            `;
          }
          if (this.input) {
            this.input.disabled = true;
          }
        } else {
          if (this.applyButton) {
            this.applyButton.disabled = false;
            // Restore original button content
            if (this._originalButtonContent) {
              this.applyButton.innerHTML = this._originalButtonContent;
              this._originalButtonContent = null;
            }
            // Remove gray-out classes if they exist
            this.applyButton.classList.remove('opacity-50', 'cursor-not-allowed');
          }
          if (this.input) {
            this.input.disabled = false;
          }
        }
      }

      showError(message) {
        // Keep the field expanded so the user can correct the code.
        if (this.toggleBtn) this.setAttribute('data-expanded', 'true');
        this.hideMessages();
        if (this.errorContainer) {
          this.errorContainer.textContent = message;
          this.errorContainer.classList.remove('hidden');
        }
      }

      showSuccess(message) {
        this.hideMessages();
        if (this.successContainer) {
          this.successContainer.textContent = message;
          this.successContainer.classList.remove('hidden');
        }
      }

      hideMessages() {
        if (this.errorContainer) {
          this.errorContainer.textContent = '';
          this.errorContainer.classList.add('hidden');
        }
        if (this.successContainer) {
          this.successContainer.textContent = '';
          this.successContainer.classList.add('hidden');
        }
      }

      async handleApply() {
        const discountCode = this.input.value.trim().toUpperCase();
        
        if (!discountCode) {
          this.showError('Please enter a discount code');
          return;
        }

        this.hideMessages();
        this.toggleLoadingState(true);

        try {
          const sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];
          
          const response = await fetch(`${routes.cart_update_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({
              discount: discountCode,
              sections: sections,
              sections_url: window.location.pathname
            })
          });

          const cartData = await response.json();

          // Check for errors first - Shopify might return errors in different formats
          if (!response.ok || cartData.errors || (cartData.status && cartData.status !== 200)) {
            let errorMessage = 'Unable to find a valid discount matching the code entered';
            
            // Try different error formats
            if (cartData.errors) {
              if (typeof cartData.errors === 'string') {
                errorMessage = cartData.errors;
              } else if (cartData.errors.discount) {
                errorMessage = cartData.errors.discount;
              } else if (cartData.errors.discount_code) {
                errorMessage = cartData.errors.discount_code;
              } else if (Array.isArray(cartData.errors) && cartData.errors.length > 0) {
                errorMessage = cartData.errors[0];
              } else if (typeof cartData.errors === 'object') {
                // Try to get first error value
                const firstError = Object.values(cartData.errors)[0];
                errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
              }
            } else if (cartData.message) {
              errorMessage = cartData.message;
            } else if (cartData.description) {
              errorMessage = cartData.description;
            }
            
            this.showError(errorMessage);
            
            if (this.input) {
              this.input.focus();
              // Restore the input value so user can see what they entered
              this.input.value = discountCode;
            }
            return;
          }

          // Validate using discount_codes.applicable — works for all discount types
          try {
            const discountEntry = (cartData.discount_codes || []).find(
              d => (d.code || '').toUpperCase().trim() === discountCode
            );

            if (discountEntry && !discountEntry.applicable) {
              this.showError("Discount code isn't valid for the items in your cart");
              if (this.input) {
                this.input.focus();
                this.input.value = discountCode;
              }
              return;
            }

            // Set flag so the new cart-discount element shows success after re-render
            window.__discountJustApplied = true;
          } catch (e) {
            // Validation failed — still update cart, don't block checkout
          }

          // Update cart drawer (re-renders the section HTML)
          if (this.cart) {
            this.cart.renderContents(cartData);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-discount',
              cartData: cartData
            });
          } else {
            window.location.reload();
          }
        } catch (error) {
          this.showError('Failed to apply discount. Please try again.');
          if (this.input) {
            this.input.focus();
            this.input.value = discountCode;
          }
        } finally {
          this.toggleLoadingState(false);
        }
      }
    }
  );
}